import { Input } from '@aigc/antd';
import { VideoThumbnail } from '@aigc/components';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import type { Pagination } from '@aigc/types';
import {
  CopyOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  VideoCameraTwoTone,
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, App, Button, Form, Table, Upload } from 'antd';
import { clsx } from 'clsx';
import copy from 'copy-to-clipboard';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import { Permission } from '../../../features/common/permision';
import {
  createBeforeUpload,
  createUploadValidator,
} from '../../../features/file';
import { usePreviewFn } from '../../../features/file/hooks';
import { fileSchema } from '../../../features/file/schema';
import { getImageUrls } from '../../../features/file/utils';
import { getScriptsQueryOptions } from '../../../features/script/api';
import { ScriptStatus, ScriptType } from '../../../features/script/model';
import { analysisVideo } from '../../../features/video-analysis/api';
import { TaskStatus } from '../../../features/video-analysis/compoents/task-result';
import { useVideoAnalysisPageData } from '../../../features/video-analysis/store';
import { MultiPromptTable } from '../../../features/video/components/multi-prompt-table';
import { NavigateButton } from '../../../features/video/components/navigate-button';
import { buildMultiPrompt } from '../../../features/video/utils';

const VideoAnalysis = () => {
  const { message, notification } = App.useApp();
  const onPreview = usePreviewFn();
  const [pageData, setVideoAnalysisPageData] = useVideoAnalysisPageData();
  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(
      object({
        product_name: string().trim().nullable().required('请输入产品名称'),
        image_url: fileSchema,
        video_file: fileSchema
          .label('视频')
          .meta({ select: true })
          .min(1, '请上传一个视频')
          .required(),
      }),
    ),
  });
  const analysis = useMutation(analysisVideo);

  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 10,
  });
  const analysisQueryResult = useQuery({
    ...getScriptsQueryOptions(false, pagination, {
      type: ScriptType.Video,
    }),
    placeholderData: keepPreviousData,
  });

  const handleStartAnalysis = handleSubmit(async (values) => {
    lfq('track', 'video.script');

    await analysis.mutateAsync({
      product_name: values.product_name,
      image_url: values.image_url?.at(0)?.response?.file_url ?? null,
      video_url: values.video_file.at(0)?.response?.file_url as string,
      type: ScriptType.Video,
    });

    message.success('视频分析任务创建成功');

    analysisQueryResult.refetch();

    reset({
      product_name: '',
      image_url: null,
      video_file: [],
    });
  });

  const acceptVideo = useAccept(['video/*']);
  useAutoPaste('#videoUploader', {
    filter: acceptVideo.filter,
  });

  const acceptImage = useAccept(['.jpg', '.jpeg', '.png', '.webp']);
  useAutoPaste('#imageUploader', {
    filter: acceptImage.filter,
  });

  return (
    <div className="flex h-full w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:flex-row sm:divide-x sm:divide-y-0">
      {/* 左侧操作面板 */}
      <div className="card flex w-full flex-none flex-col sm:overflow-auto xl:w-[480px]">
        <Form layout="vertical">
          <FormControl
            control={control}
            name="product_name"
            label="产品名称"
            required
            render={({ field }) => (
              <Input
                {...field}
                placeholder="请输入产品名称，中英文均支持"
              ></Input>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="image_url"
            exact
            render={({ field }) => (
              <Upload.Dragger
                id="imageUploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                listType="picture"
                fileList={field.value ?? []}
                maxCount={1}
                accept=".jpg,.jpeg,.png,.webp"
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({
                    accept: '.jpg,.jpeg,.png,.webp',
                  }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                onPreview={onPreview}
                onChange={(info) => {
                  const { status } = info.file;

                  if (status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `图片 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <PictureOutlined />
                </p>
                <p className="ant-upload-text">
                  点击、拖放或
                  <span className="text-blue-500">粘贴</span>上传图片
                </p>
                <p className="ant-upload-hint">上传产品图 (选填，白底图最佳)</p>
              </Upload.Dragger>
            )}
          ></FormControl>
          <FormControl
            control={control}
            name="video_file"
            exact
            render={({ field }) => (
              <Upload.Dragger
                id="videoUploader"
                ref={field.ref}
                name={field.name}
                disabled={field.disabled}
                classNames={{ list: 'mt-2' }}
                listType="picture"
                fileList={field.value}
                iconRender={() => <VideoCameraTwoTone />}
                beforeUpload={createBeforeUpload({
                  validator: createUploadValidator({ accept: '.mp4' }),
                  onError: ({ title, message }) => {
                    notification.error({ title, description: message });
                  },
                })}
                maxCount={1}
                accept=".mp4"
                onPreview={onPreview}
                onChange={(info) => {
                  const { status } = info.file;

                  if (status === 'error') {
                    notification.error({
                      title: '错误',
                      description: `视频 ${info.file.name} 上传失败，请重试`,
                    });
                  }

                  field.onChange(info.fileList);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <VideoCameraOutlined />
                </p>
                <p className="ant-upload-text">
                  点击、拖放或<span className="text-blue-500">粘贴</span>
                  上传视频
                </p>
              </Upload.Dragger>
            )}
          ></FormControl>
          {/* 分析按钮 */}
          <Button
            variant="solid"
            color="primary"
            size="large"
            loading={analysis.isPending}
            block
            onClick={handleStartAnalysis}
          >
            🚀 {analysis.isPending ? '分析中' : '开始分析'}
          </Button>
        </Form>

        {/* 历史记录 */}
        <Table
          classNames={{ root: 'mt-4' }}
          size="small"
          tableLayout="fixed"
          title={() => <span className="font-bold">历史分析</span>}
          showHeader={false}
          dataSource={analysisQueryResult.data?.history}
          rowKey={(row) => row.id}
          onRow={(row) => ({
            className: clsx('cursor-pointer', {
              '!bg-primary-200': pageData?.id === row.id,
            }),
            onClick: () => {
              if (
                row.status !== ScriptStatus.成功 &&
                row.status !== ScriptStatus.失败
              ) {
                message.info('视频正在分析中，请稍候');
                return;
              }

              setVideoAnalysisPageData(row);
            },
          })}
          columns={[
            {
              width: 80,
              render: (_, row) => (
                <VideoThumbnail
                  size={64}
                  src={row.data.filename}
                ></VideoThumbnail>
              ),
            },
            {
              render: (_, item) => {
                return (
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center">
                      <div className="min-w-0 truncate text-base">
                        {item.data.product_name}
                      </div>
                      <div className="flex-auto"></div>
                      <div className="flex-none text-xs">
                        {new Date(item.created_at).toLocaleString()}
                      </div>
                    </div>
                    {item.data.product_description && (
                      <p className="line-clamp-3 text-xs">
                        {item.data.product_description}
                      </p>
                    )}
                    <TaskStatus script={item}></TaskStatus>
                    {item.status === ScriptStatus.失败 && (
                      <Alert
                        type="error"
                        title={item.error ?? '分析出错'}
                      ></Alert>
                    )}
                  </div>
                );
              },
            },
          ]}
          pagination={{
            total: analysisQueryResult.data?.total ?? 0,
            current: pagination.page,
            pageSize: pagination.size,
            onChange: (page, pageSize) => {
              setPagination({
                page,
                size: pageSize,
              });
            },
            simple: true,
            hideOnSinglePage: true,
          }}
          rowHoverable={false}
        ></Table>
      </div>

      {/* 右侧结果展示 */}
      <div className="flex flex-none flex-col overflow-y-auto sm:flex-auto">
        <h2 className="flex-none p-4 text-xl font-bold">视频分析结果</h2>
        {pageData?.data.id ? (
          <div className="card min-h-0 flex-auto animate-slide-up overflow-y-auto">
            <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 flex-auto">
                <h3 className="truncate text-xl font-bold text-primary-800">
                  {pageData.data.product_name ??
                    getImageUrls(pageData.data.file_list)?.at(0)}
                </h3>
                {pageData.data.product_description && (
                  <p>{pageData.data.product_description}</p>
                )}
              </div>
            </div>

            {/* 首尾帧展示 */}
            {!!pageData.data.filename && (
              <div className="mb-4 rounded-xl border border-primary-200 bg-white p-4">
                <p className="mb-2 font-medium text-primary-500">源视频</p>
                <div className="relative !aspect-video !h-[252px] bg-black">
                  <video
                    className="h-full w-full object-contain"
                    src={
                      URL.canParse(pageData.data.filename)
                        ? pageData.data.filename
                        : void 0
                    }
                    autoPlay
                    playsInline
                    muted
                    loop
                    controls
                  ></video>
                </div>
              </div>
            )}

            <div className="mb-4 rounded-xl border border-primary-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {pageData.data.user_profile && (
                  <div className="rounded-lg bg-primary-50 p-3">
                    <span className="text-sm text-primary-500">人物形象：</span>
                    <span className="font-medium text-primary-800">
                      {pageData.data.user_profile}
                    </span>
                  </div>
                )}

                {pageData.data.context && (
                  <div className="rounded-lg bg-primary-50 p-3">
                    <span className="text-sm text-primary-500">环境：</span>
                    <span className="font-medium text-primary-800">
                      {pageData.data.context}
                    </span>
                  </div>
                )}

                {pageData.data.regional_features && (
                  <div className="rounded-lg bg-primary-50 p-3">
                    <span className="text-sm text-primary-500">区域背景：</span>
                    <span className="font-medium text-primary-800">
                      {pageData.data.regional_features}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* 脚本内容 */}
            <div className="border-t border-primary-200 pt-6">
              <div className="flex items-center p-2">
                <div className="text-lg">📝 脚本解析</div>
                <div className="flex-auto"></div>
                <div className="space-x-2">
                  <Button
                    variant="solid"
                    color="default"
                    icon={<CopyOutlined></CopyOutlined>}
                    onClick={() => {
                      copy(buildMultiPrompt(pageData.data.segments), {
                        format: 'text/plain',
                        onCopy: () => {
                          message.success('已复制内容到剪贴板');
                        },
                      });
                    }}
                  ></Button>
                  <NavigateButton
                    script={pageData.data}
                    variant="solid"
                    color="primary"
                  ></NavigateButton>
                </div>
              </div>
              {pageData.data.segments?.length || pageData.data.extra_prompt ? (
                <MultiPromptTable
                  segments={pageData.data.segments}
                  extraPrompt={pageData.data.extra_prompt}
                ></MultiPromptTable>
              ) : (
                <div className="rounded-xl border border-primary-200/50 bg-white py-12 text-center text-primary-500">
                  <p>📭 暂无解析结果</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 text-6xl opacity-50">🎥</div>
            <p className="text-lg text-primary-500">请上传视频并点击开始分析</p>
            <p className="mt-2 text-sm text-primary-500">
              系统将自动分析视频内容，并生成完整脚本
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_creator-center/video/analysis')({
  staticData: { permissions: [Permission.视频生成] },
  component: VideoAnalysis,
});
